import type { UserEmailDuplicateFragment } from "@/v2-backoffice-client/user/emailDuplicate/__types__/user.emailDuplicate.fragment"

/** acc.ChangeSources: who created the row. */
const CHANGE_SOURCE_INVITATION = 2
const CHANGE_SOURCE_CONSOLE = 3

/**
 * Which of the rows sharing an email to keep: the one the person has
 * signed in with (latest sign-in wins), else the one with the most teams,
 * else the oldest. The others are merged into it; the operator can pick
 * any row instead.
 */
export function suggestKeeper(
  rows: UserEmailDuplicateFragment[],
): UserEmailDuplicateFragment | null {
  if (rows.length === 0) return null
  return [...rows].sort((a, b) => {
    const signInA = a.LastSignInAt ? Date.parse(a.LastSignInAt) : 0
    const signInB = b.LastSignInAt ? Date.parse(b.LastSignInAt) : 0
    if (signInA !== signInB) return signInB - signInA
    if (a.TenantCount !== b.TenantCount) return b.TenantCount - a.TenantCount
    const createdA = a.CreatedAt ? Date.parse(a.CreatedAt) : 0
    const createdB = b.CreatedAt ? Date.parse(b.CreatedAt) : 0
    return createdA - createdB
  })[0]
}

/** Rows grouped by email, each group sorted by id, in email order. */
export function groupDuplicatesByEmail(rows: UserEmailDuplicateFragment[]) {
  const groups = new Map<string, UserEmailDuplicateFragment[]>()
  for (const row of rows) {
    const key = row.Email.toLowerCase()
    groups.set(key, [...(groups.get(key) ?? []), row])
  }
  return [...groups.values()].map((group) =>
    [...group].sort((a, b) => a.Id - b.Id),
  )
}

/** "Fusion login" / "B2C login" / "Fusion and B2C logins" / "no login", for the row and the confirmation. */
export function describeDuplicateLogins(row: UserEmailDuplicateFragment) {
  if (row.HasEntraId && row.HasB2cIdentity) return "Fusion and B2C logins"
  if (row.HasEntraId) return "Fusion login"
  if (row.HasB2cIdentity) return "B2C login"
  return "no login"
}

/**
 * Where the row came from: created in the Backoffice (by whom, when known
 * to the caller), or a sign-up through an invitation.
 */
export function describeDuplicateOrigin(row: UserEmailDuplicateFragment) {
  if (row.ChangeSourceCode === CHANGE_SOURCE_INVITATION) {
    return "Invitation sign-up"
  }
  if (row.ChangeSourceCode === CHANGE_SOURCE_CONSOLE) return "Created in Fusion"
  return row.CreatedByUserId
    ? `Created in Backoffice by #${row.CreatedByUserId}`
    : "Created in Backoffice"
}

/**
 * What merging `retired` into `kept` moves, in the words the confirmation
 * uses: the identity the kept row lacks, the teams, the named seats, the
 * CRM link; and what is dropped because the kept row already has an
 * identity in that directory. A seat both rows hold on one license is the
 * kept row's already and is not announced.
 */
export function describeMergeEffects(
  kept: UserEmailDuplicateFragment,
  retired: UserEmailDuplicateFragment,
) {
  const moves: string[] = []
  const drops: string[] = []
  if (retired.HasEntraId) {
    if (kept.HasEntraId) drops.push("its Fusion login")
    else moves.push("the Fusion login")
  }
  if (retired.HasB2cIdentity) {
    if (kept.HasB2cIdentity) drops.push("its B2C login")
    else moves.push("the B2C login")
  }
  if (retired.TenantCount > 0) {
    moves.push(
      `its ${retired.TenantCount} team membership${retired.TenantCount === 1 ? "" : "s"}`,
    )
  }
  if (retired.SeatCount > 0) {
    moves.push(
      `its ${retired.SeatCount} named seat${retired.SeatCount === 1 ? "" : "s"}`,
    )
  }
  if (retired.HasHubspotContact && !kept.HasHubspotContact) {
    moves.push("the HubSpot contact link")
  }
  return { moves, drops }
}
